import { useCallback, useRef, useState } from "react";
import { conversationRow, messagesFromConversation } from "../../../lib/artemis/conversations";
import { pollFrom } from "../../../lib/artemis/group";

// Saved chats (API-3641): the chat list, reopening a chat with its earlier pages, and deleting.
// api holds the dispatched v3 actions from the panel's props.

const useSavedChats = api => {
  const [rows, setRows] = useState([])
  const [cursor, setCursor] = useState(null)
  // 'idle', 'loading', 'loaded' or 'failed'.
  const [listState, setListState] = useState('idle')
  const [earlierBeforeSeq, setEarlierBeforeSeq] = useState(null)
  const [loadingEarlier, setLoadingEarlier] = useState(false)
  // Every raw message loaded for the open chat, so a reply and its question pair up across pages.
  const raw = useRef([])
  // Whether the open chat is a group, so messages that skipped Artemis read as posted.
  const group = useRef(false)

  const loadList = useCallback((more = false) => {
    setListState('loading')
    return api.fetchConversations(more ? cursor : null)
      .then(page => {
        const items = ((page && page.Items) || []).map(conversationRow)
        setRows(current => (more ? [...current, ...items] : items))
        setCursor((page && page.NextCursor) || null)
        setListState('loaded')
      })
      .catch(() => setListState('failed'))
  }, [api, cursor])

  // Resolves with the chat's messages, oldest first, or null when it's gone (404: deleted, or left).
  const open = useCallback((conversationId, options = {}) => {
    raw.current = []
    group.current = !!options.group
    setEarlierBeforeSeq(null)
    return api.fetchMessages(conversationId, null)
      .then(page => {
        raw.current = (page && page.Items) || []
        setEarlierBeforeSeq(page && page.NextBeforeSeq != null ? page.NextBeforeSeq : null)
        return messagesFromConversation(raw.current, { group: group.current })
      })
      .catch(err => {
        if (err && err.code === 'http_404') {
          setRows(current => current.filter(r => r.id !== conversationId))
          return null
        }
        throw err
      })
  }, [api])

  // Resolves with every loaded message, oldest first, including the page before the earliest.
  const loadEarlier = useCallback(conversationId => {
    if (earlierBeforeSeq == null) return Promise.resolve(null)
    setLoadingEarlier(true)
    return api.fetchMessages(conversationId, earlierBeforeSeq)
      .then(page => {
        raw.current = [...((page && page.Items) || []), ...raw.current]
        setEarlierBeforeSeq(page && page.NextBeforeSeq != null ? page.NextBeforeSeq : null)
        return messagesFromConversation(raw.current, { group: group.current })
      })
      .finally(() => setLoadingEarlier(false))
  }, [api, earlierBeforeSeq])

  // Group chats: fetches what's new since the last poll (artemisapi afterSeq), replaces messages by Id,
  // and resolves with { turns, lastSeq, typing } for the whole chat; turns is null when no message changed.
  // typing is who else is typing now ([{ UserId, Name }]).
  const poll = useCallback(conversationId => {
    const after = pollFrom(raw.current)
    return api.pollMessages(conversationId, after).then(page => {
      const items = (page && page.Items) || []
      const typing = (page && Array.isArray(page.Typing)) ? page.Typing : []
      if (items.length === 0) return { turns: null, lastSeq: null, typing }
      const byId = new Map(raw.current.map(m => [m.Id, m]))
      items.forEach(m => byId.set(m.Id, m))
      raw.current = [...byId.values()]
      const seqs = raw.current.map(m => m.Seq).filter(Number.isFinite)
      return { turns: messagesFromConversation(raw.current, { group: true }), lastSeq: seqs.length ? Math.max(...seqs) : 0, typing }
    })
  }, [api])

  // A message this user posted to a group (no stream): kept so polling knows it's loaded.
  const addRaw = useCallback(message => { if (message && message.Id) raw.current = [...raw.current.filter(m => m.Id !== message.Id), message] }, [])

  // The Seqs of the loaded messages the user didn't write (alerts, Artemis, partners): what UnreadCount counts.
  const othersSeqs = () => raw.current.filter(m => m.AuthorType !== 'user').map(m => m.Seq).filter(Number.isFinite)

  const lastSeq = () => {
    const seqs = raw.current.map(m => m.Seq).filter(Number.isFinite)
    return seqs.length ? Math.max(...seqs) : 0
  }

  const forgetEarlier = useCallback(() => {
    raw.current = []
    group.current = false
    setEarlierBeforeSeq(null)
  }, [])

  const remove = useCallback(conversationId =>
    api.deleteConversation(conversationId).then(() => setRows(current => current.filter(r => r.id !== conversationId))), [api])

  return { rows, hasMore: !!cursor, listState, loadList, open, loadEarlier, hasEarlier: earlierBeforeSeq != null, loadingEarlier, forgetEarlier, remove, poll, addRaw, lastSeq, othersSeqs }
}

export default useSavedChats
